<template>
  <div class="container-fluid">
    <!-- ---------------------------- -->
    <!-- OVERLAY: artefact panel      -->
    <!-- ---------------------------- -->
    <ArtefactSidePanel
      v-if="isSidePanelOpen"
      :artefact="artefact"
      :artefactNames="artefactNames"
      @add-campaign-artefact="addCampaignArtefactHarness"
      @update-campaign-artefact="updateCampaignArtefactHarness"
      @delete-campaign-artefact="deleteCampaignArtefactHarness"
      @close-artefact-panel="handleArtefactPanelClose"
    />
    <!-- ------------------------ -->
    <!-- NOTIFICATIONS            -->
    <!-- ------------------------ -->
    <notifications
      width="500"
      group="bb-confirm"
      :speed="100"
      :duration="1000"
      position="top right"
      classes="vue-notification"
    />
    <!-- ------------------- -->
    <!-- MAIN ROW            -->
    <!-- ------------------- -->
    <div class="row">
      <!-- -------------------- -->
      <!-- COL 1: buttons panel -->
      <!-- -------------------- -->
      <div class="col-1 rounded">
        <ArtefactButtonsPanel
          :canScrollLeft="artefactsCanScrollLeft"
          :canScrollRight="artefactsCanScrollRight"
          :canScrollDown="artefactsCanScrollDown"
          :canScrollUp="artefactsCanScrollUp"
          @scroll-table="handleArtefactsScrollTable"
        />
      </div>
      <!-- ------------------------------ -->
      <!-- COL 2: campaign-artefact table -->
      <!-- ------------------------------ -->
      <div class="col-11">
        <TableArtefacts
          ref="TableArtefacts"
          v-if="artefacts && loadReady"
          :headings="headings"
          :artefacts="artefacts"
          :searchQuery="searchQuery"
          @refresh-scroll-state="refreshArtefactsScrollState"
          @show-artefact-panel="handleShowArtefactPanel"
          @delete-campaign-artefact="deleteCampaignArtefactHarness"
        />
      </div>
    </div>
    <div
      v-if="!loadReady"
      class="bb-loader"
    ></div>
  </div>
</template>

<script>
//========================================================
// scripts
//---------
import { inject } from 'vue'
import { mapActions, mapGetters, mapMutations } from 'vuex'

import TableArtefacts from '../components/TableArtefacts.vue'
import ArtefactSidePanel from '../components/ArtefactSidePanel.vue'
import ArtefactButtonsPanel from '../components/ArtefactButtonsPanel.vue'

import useEditCampaignArtefact from '../composables/useEditCampaignArtefact'

import showNotification from '../helpers/showNotification'
import getPrevious from '../helpers/getPrevious'
import handleError from '../helpers/handleError'
import getNext from '../helpers/getNext'

export default {
  name: 'CampaignArtefactsView',
  //========================================================
  // setup
  //-------
  setup() {
    const global = inject('global')
    return { global }
  },
  //========================================================
  // components
  //------------
  components: {
    TableArtefacts,
    ArtefactSidePanel,
    ArtefactButtonsPanel,
  },
  //========================================================
  // data
  //------
  data() {
    return {
      endpoint: '',
      local_slug: '',
      deleteInd: false,
      isSidePanelOpen: false,
      loadReady: false,
      arena: null,
      artefact: null,
      config: null,
      artefacts: [],
      errors: [],
      errorFields: [],
      name: 'ArtefactList',
      searchQuery: '',
      artefactsCanScrollLeft: false,
      artefactsCanScrollRight: true,
      artefactsCanScrollDown: false,
      artefactsCanScrollUp: false,
      updateChainsByArtefactId: {},
      headings: [
        // -------------------------------------------------------------------
        // basic details
        {
          id: 0,
          label: '#',
          field: 'rowNumber',
          filterable: false,
          sortable: true,
          tdClass: 'text-center',
          width: '4em',
        },
        {
          id: 5,
          label: 'Artefact',
          field: 'name',
          width: '14em',
          filterable: false,
          sortable: true,
          formatFn: (value, row) => {
            return row.display_name || row.name
          },
        },
        {
          id: 6,
          label: 'Image',
          field: 'image',
          width: '6em',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: false,
        },
        {
          id: 7,
          label: 'Type',
          field: 'artefact_type',
          tdClass: 'text-center',
          filterable: true,
          sortable: true,
          hidden: false,
        },
        {
          id: 65,
          label: 'Campaign Ref',
          field: 'campaign_ref',
          tdClass: 'text-center',
          filterable: true,
          sortable: true,
          formatFn: (value, row) => {
            return row.campaign_ref > 0 ? row.campaign_ref : ''
          },
          width: '6em',
        },
        {
          id: 155,
          label: 'Active',
          field: 'active_image',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '6em',
        },
        {
          id: 156,
          label: 'Status',
          field: 'status',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '10em',
        },
        {
          id: 10,
          label: 'Short Desc',
          field: 'short_description',
          // tdClass: 'text-nowrap',
          width: '20em',
          filterable: false,
          sortable: false,
          hidden: false,
        },
        // -------------------------------------------------------------------
        // owners
        {
          id: 15,
          label: '',
          field: 'fill0',
          filterable: false,
          sortable: false,
          tdClass: 'bb-is-fill',
          thClass: 'bb-is-fill',
        },
        {
          id: 35,
          label: 'Owning Player',
          field: 'player_name',
          tdClass: 'text-center',
          filterable: false,
          sortable: true,
        },
        {
          id: 36,
          label: 'Owning Character',
          field: 'character_name',
          tdClass: 'text-center',
          filterable: false,
          sortable: true,
        },
        // -------------------------------------------------------------------
        // descriptors
        {
          id: 40,
          label: '',
          field: 'fill1',
          filterable: false,
          sortable: false,
          tdClass: 'bb-is-fill',
          thClass: 'bb-is-fill',
        },
        {
          id: 100,
          label: 'Power Storage',
          field: 'pwr_storage',
          tdClass: 'text-center',
          width: '6em',
          filterable: true,
          sortable: true,
          hidden: false,
          formatFn: (value) => {
            const numericValue = Number(value)
            return Number.isFinite(numericValue) && numericValue > 0
              ? numericValue
              : ''
          },
        },
        {
          id: 100,
          label: 'Material',
          field: 'material',
          tdClass: 'text-center',
          filterable: true,
          sortable: true,
          hidden: false,
        },
        {
          id: 100,
          label: 'Quality',
          field: 'quality',
          tdClass: 'text-center',
          filterable: true,
          sortable: true,
          hidden: false,
        },
        {
          id: 100,
          label: 'Age',
          field: 'age',
          tdClass: 'text-center',
          filterable: true,
          sortable: true,
          hidden: false,
        },
        {
          id: 100,
          label: 'Race',
          field: 'race',
          tdClass: 'text-center',
          filterable: true,
          sortable: true,
          hidden: false,
          // display race name instead of numeric id
          formatFn: (value) => {
            const getRaceById = this.$store.getters['races/getRaceById']
            const race =
              typeof getRaceById === 'function' ? getRaceById(value) : null
            return race ? race.name : 'Unknown'
          },
        },
        // -------------------------------------------------------------------
        // counts
        {
          id: 103,
          label: '',
          field: 'fill4',
          filterable: false,
          sortable: false,
          tdClass: 'bb-is-fill',
          thClass: 'bb-is-fill',
        },
        {
          id: 55,
          label: 'Total Enc',
          field: 'tot_enc',
          type: 'number',
          tdClass: 'text-center',
          filterable: false,
          sortable: true,
          formatFn: (value) => {
            const numericValue = Number(value)
            return Number.isFinite(numericValue) ? numericValue.toFixed(2) : ''
          },
          width: '6em',
        },
        {
          id: 59,
          label: 'Tally Qty',
          field: 'tally_qty',
          tdClass: 'text-center',
          filterable: false,
          sortable: true,
          width: '6em',
          formatFn: (value, row) => {
            return row.tallyable ? (value ?? '') : ''
          },
        },
        {
          id: 57,
          label: 'Unit Name',
          field: 'unit_name',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '7em',
          formatFn: (value, row) => {
            return row.countable ? value || '' : ''
          },
        },
        {
          id: 59,
          label: 'Max Units',
          field: 'max_unit_count',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '6em',
          formatFn: (value, row) => {
            return row.countable ? (value ?? '') : ''
          },
        },
        // -------------------------------------------------------------------
        // tick images
        {
          id: 103,
          label: '',
          field: 'fill4',
          filterable: false,
          sortable: false,
          tdClass: 'bb-is-fill',
          thClass: 'bb-is-fill',
        },
        {
          id: 145,
          label: 'Tallyable',
          field: 'tallyable_image',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '6em',
        },
        {
          id: 160,
          label: 'Countable',
          field: 'countable_image',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '6em',
        },
        {
          id: 155,
          label: 'Wearable',
          field: 'wearable_image',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '6em',
        },
        {
          id: 165,
          label: 'Storeable',
          field: 'storeable_image',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '6em',
        },

        {
          id: 175,
          label: 'Storage',
          field: 'storage_image',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '6em',
        },

        {
          id: 185,
          label: 'Attachable',
          field: 'attachable_image',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '6em',
        },

        {
          id: 195,
          label: 'Attach Point',
          field: 'attach_point_image',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '6em',
        },

        // --------------------------------------------------------------
        // misc field (hidden)
        {
          id: 105,
          label: 'Id',
          field: 'id',
          type: 'number',
          tdClass: 'text-nowrap',
          hidden: true,
        },

        // --------------------------------------------------------------
        // item flags (hidden)
        {
          id: 130,
          label: 'Active Flag',
          field: 'active',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        },
        {
          id: 150,
          label: 'Tallyable Flag',
          field: 'tallyable',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        },
        {
          id: 170,
          label: 'Storeable Flag',
          field: 'storeable',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        },
        {
          id: 160,
          label: 'Wearable Flag',
          field: 'wearable',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        },
        {
          id: 190,
          label: 'Attachable Flag',
          field: 'attachable',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        },
        {
          id: 180,
          label: 'Storage Flag',
          field: 'storage',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        },
        {
          id: 200,
          label: 'Attach Point Flag',
          field: 'attach_point',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        },
        {
          id: 100,
          label: 'Impale Flag',
          field: 'impale',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        },
      ],
    }
  },
  //========================================================
  // computed
  //----------
  computed: {
    artefactNames() {
      const names_array = this.artefacts.map(function (array) {
        return array.name
      })
      return names_array
    },
    isInvertedSidePanelOpen() {
      return !this.isSidePanelOpen
    },
    api_request() {
      return window.bbAPIRequest(this.endpoint, this.local_slug, this.deleteInd)
    },
  },
  //========================================================
  // methods
  //---------
  methods: {
    //--------------------
    // ref mapped methods
    //--------------------
    ...mapMutations('auth', {
      setCurrentView: 'setCurrentView',
      setMessage: 'setMessage',
      destroyToken: 'destroyToken',
      destroyUserProfile: 'destroyUserProfile',
    }),
    ...mapGetters('combat', {
      getCampaign: 'getCampaign',
      getConfig: 'getConfig',
    }),
    ...mapGetters('campaignArtefacts', {
      getCampaignArtefacts: 'getCampaignArtefacts',
    }),
    ...mapActions('combat', {
      loadConfig: 'loadConfig',
    }),
    ...mapActions('campaignArtefacts', {
      loadStaticCampaignArtefacts: 'loadStaticCampaignArtefacts',
    }),
    //========================================================
    // handle artefact panel close
    //-----------------------------
    handleArtefactPanelClose() {
      console.log('IN handleArtefactPanelClose...')
      // this.SetView('items')
      this.isSidePanelOpen = this.isInvertedSidePanelOpen
    },
    //========================================================
    // hide zeros
    //------------
    hideZeros(rowObj) {
      if (rowObj.damage_mod == '0') {
        return ''
      } else {
        return rowObj.damage_mod
      }
    },
    //========================================================
    // exit view
    //-----------
    exitView() {
      this.emitter.off('add-artefact')
      this.emitter.off('update-artefact')
      this.emitter.off('delete-artefact')
      this.emitter.off('show-artefact-panel')
      this.emitter.off('exit-artefact-view')

      this.setView('')
      this.$router.go(-1)
    },
    //========================================================
    // get campaign handler
    //----------------------
    getCampaignHandler() {
      this.arena = this.getCampaign()
    },
    //========================================================
    // show artefact panel
    //---------------------
    showArtefactPanel() {
      this.artefact = null
      this.isSidePanelOpen = this.isInvertedSidePanelOpen
    },
    //========================================================
    // handle show artefact panel
    //----------------------------
    handleShowArtefactPanel(artefact) {
      this.artefact = artefact ?? null
      this.isSidePanelOpen = true
    },
    handleArtefactsScrollTable(dir) {
      const tableRef = this.$refs.TableArtefacts
      if (!tableRef || typeof tableRef.handleScrollTable !== 'function') {
        return
      }

      tableRef.handleScrollTable(dir)

      setTimeout(() => {
        this.refreshArtefactsScrollState()
      }, 380)
    },
    refreshArtefactsScrollState() {
      const tableRef = this.$refs.TableArtefacts
      if (!tableRef) {
        this.artefactsCanScrollLeft = false
        this.artefactsCanScrollRight = true
        this.artefactsCanScrollDown = false
        this.artefactsCanScrollUp = false
        return
      }

      if (typeof tableRef.getHorizontalScrollState === 'function') {
        const horizontalState = tableRef.getHorizontalScrollState()
        this.artefactsCanScrollLeft = !!horizontalState?.canScrollLeft
        this.artefactsCanScrollRight = !!horizontalState?.canScrollRight
      }

      if (typeof tableRef.getVerticalScrollState === 'function') {
        const verticalState = tableRef.getVerticalScrollState()
        this.artefactsCanScrollDown = !!verticalState?.canScrollDown
        this.artefactsCanScrollUp = !!verticalState?.canScrollUp
      }
    },
    //========================================================
    // set view
    //----------
    setView(view) {
      this.setCurrentView(view)
    },
    isTemporaryClientId(value) {
      return (
        typeof value === 'string' &&
        value.trim().toUpperCase().startsWith('TMP-')
      )
    },
    isUnknownRelatedSentinel(value) {
      return typeof value === 'string' && value.trim() === '__unknown_race__'
    },
    normaliseRelatedField(value) {
      if (value === undefined || value === null || value === '') {
        return null
      }

      if (typeof value === 'object') {
        const candidate = value.id ?? value.short_name ?? value.name ?? null
        return this.isTemporaryClientId(candidate) ||
          this.isUnknownRelatedSentinel(candidate)
          ? null
          : candidate
      }

      if (
        this.isTemporaryClientId(value) ||
        this.isUnknownRelatedSentinel(value)
      ) {
        return null
      }

      return value
    },
    normaliseBooleanField(value, fallback = false) {
      if (value === undefined || value === null) {
        return fallback
      }

      if (typeof value === 'boolean') {
        return value
      }

      if (typeof value === 'number') {
        return value !== 0
      }

      if (typeof value === 'string') {
        const normalised = value.trim().toLowerCase()
        if (['yes', 'true', '1', 'on'].includes(normalised)) {
          return true
        }
        if (['no', 'false', '0', 'off', ''].includes(normalised)) {
          return false
        }
      }

      return !!value
    },
    normaliseSkillIds(skills) {
      const source = Array.isArray(skills)
        ? skills
        : skills === undefined || skills === null || skills === ''
          ? []
          : [skills]

      const seen = new Set()

      return source
        .map((entry) => {
          if (entry && typeof entry === 'object') {
            return entry.skill ?? entry.skill_id ?? entry.id ?? null
          }

          return entry
        })
        .map((entry) => this.normaliseRelatedField(entry))
        .filter((entry) => {
          if (!entry || seen.has(entry)) {
            return false
          }

          seen.add(entry)
          return true
        })
    },
    normaliseSkillDetails(skills) {
      const source = Array.isArray(skills)
        ? skills
        : skills === undefined || skills === null || skills === ''
          ? []
          : [skills]

      const seen = new Set()

      return source
        .map((entry) => {
          const skillId =
            entry && typeof entry === 'object'
              ? (entry.skill ?? entry.skill_id ?? entry.id ?? null)
              : entry
          const normalisedSkillId = this.normaliseRelatedField(skillId)

          if (!normalisedSkillId || seen.has(normalisedSkillId)) {
            return null
          }

          seen.add(normalisedSkillId)

          return {
            skill: normalisedSkillId,
            min_str: Number(entry?.min_str ?? 0),
            min_dex: Number(entry?.min_dex ?? 0),
            adj_value: Number(entry?.adj_value ?? 0),
            damage_dice:
              typeof entry?.damage_dice === 'string'
                ? entry.damage_dice.trim()
                : '',
          }
        })
        .filter(Boolean)
    },
    normaliseArtefactSubtype(value) {
      if (value === undefined || value === null || value === '') {
        return ''
      }

      const candidate =
        typeof value === 'object'
          ? (value.short_name ?? value.name ?? value.label ?? value.id ?? '')
          : value

      return typeof candidate === 'string' ? candidate.trim().toLowerCase() : ''
    },
    stripSubtypeSpecificArtefactFields(payload, rawPayload = {}) {
      if (!payload || typeof payload !== 'object') {
        return payload
      }

      const subtype =
        this.normaliseArtefactSubtype(rawPayload.artefact_type) ||
        this.normaliseArtefactSubtype(rawPayload.actual_type) ||
        this.normaliseArtefactSubtype(rawPayload.effective_type) ||
        this.normaliseArtefactSubtype(
          rawPayload.type === 'Artefact' ? null : rawPayload.type,
        )

      if (!subtype) {
        return payload
      }

      const cleanedPayload = { ...payload }
      const poisonOnlyFields = [
        'poison_type',
        'pot',
        'effect',
        'duration',
        'resist',
      ]
      const healingOnlyFields = ['healing_type', 'recovers']

      if (subtype === 'healing') {
        poisonOnlyFields.forEach((key) => {
          delete cleanedPayload[key]
        })
      } else if (subtype === 'poison') {
        healingOnlyFields.forEach((key) => {
          delete cleanedPayload[key]
        })
      } else {
        ;[...poisonOnlyFields, ...healingOnlyFields].forEach((key) => {
          delete cleanedPayload[key]
        })
      }

      return cleanedPayload
    },
    normaliseArtefactRow(row, options = {}) {
      if (!row || typeof row !== 'object') {
        return row
      }

      const forceRecalculateTotEnc = !!options.recalculateTotEnc
      const existingTotEnc = Number(row.tot_enc)
      const hasNumericTotEnc = Number.isFinite(existingTotEnc)

      if (hasNumericTotEnc && !forceRecalculateTotEnc) {
        return row
      }

      const enc = Number(row.enc)
      const parsedQty = Number.parseInt(row.tally_qty, 10)
      const qty = Number.isFinite(parsedQty) && parsedQty > 0 ? parsedQty : 1

      const totEnc = Number.isFinite(enc)
        ? Number((enc * qty).toFixed(2))
        : hasNumericTotEnc
          ? existingTotEnc
          : 0

      return {
        ...row,
        tot_enc: totEnc,
      }
    },
    normaliseArtefactRows(rows = []) {
      return (Array.isArray(rows) ? rows : []).map((row) =>
        this.normaliseArtefactRow(row),
      )
    },
    formatArtefactActionMessage(artefact, action) {
      const rawName = artefact?.name
      const name =
        typeof rawName === 'string' && rawName.trim() !== ''
          ? rawName.trim()
          : 'Artefact'

      const rawCampaignRef = artefact?.campaign_ref
      const hasCampaignRef =
        rawCampaignRef !== null &&
        rawCampaignRef !== undefined &&
        String(rawCampaignRef).trim() !== ''
      const campaignRefText = hasCampaignRef
        ? String(rawCampaignRef).trim()
        : '?'

      return `${name} (Ref ${campaignRefText}) ${action}.`
    },
    buildCampaignArtefactCreatePayload(rawPayload = {}) {
      const sourceType = rawPayload.type || rawPayload.actual_type || 'Artefact'
      const payload = {
        name: rawPayload.name ?? '',
        type: 'Artefact',
        dm_notes: rawPayload.dm_notes ?? rawPayload.notes ?? '',
        short_description: rawPayload.short_description ?? '',
        description: rawPayload.description ?? '',
        active: rawPayload.active ?? true,
        status:
          rawPayload.status ?? ((rawPayload.active ?? true) ? 'Active' : null),
        campaign: true,
        starter: rawPayload.starter ?? false,
        tallyable: rawPayload.tallyable ?? false,
        tally_qty: rawPayload.tally_qty ?? 0,
        wearable: rawPayload.wearable ?? false,
        storeable: rawPayload.storeable ?? true,
        storage: rawPayload.storage ?? false,
        attachable: rawPayload.attachable ?? false,
        attach_point: rawPayload.attach_point ?? false,
        countable: rawPayload.countable ?? false,
        equipment_type: rawPayload.equipment_type ?? 'n/a',
        unit_name: rawPayload.unit_name ?? '',
        max_unit_count: rawPayload.max_unit_count ?? 0,
        ap: rawPayload.ap ?? 0,
        hp: rawPayload.hp ?? 0,
        range: rawPayload.range ?? '',
        load: rawPayload.load ?? 0,
        damage_dice: rawPayload.damage_dice ?? '',
        impale: this.normaliseBooleanField(rawPayload.impale, false),
        set_charge: this.normaliseBooleanField(rawPayload.set_charge, false),
        poison_type: rawPayload.poison_type ?? null,
        pot: rawPayload.pot ?? 0,
        effect: rawPayload.effect ?? '',
        delay: rawPayload.delay ?? '',
        duration: rawPayload.duration ?? '',
        resist: rawPayload.resist ?? '',
        healing_type: rawPayload.healing_type ?? null,
        recovers: rawPayload.recovers ?? '',
        estimated_value: rawPayload.estimated_value ?? false,
        enc: rawPayload.enc ?? 0.01,
        value_lower: rawPayload.value_lower ?? 0,
        value_upper: rawPayload.value_upper ?? 0,
        currency: this.normaliseRelatedField(rawPayload.currency),
        pwr_storage: rawPayload.pwr_storage ?? 0,
        material: rawPayload.material ?? 'Unknown',
        age: rawPayload.age ?? 'Unknown',
        quality: rawPayload.quality ?? 'Unknown',
        trait: this.normaliseRelatedField(rawPayload.trait),
        skill_ids: this.normaliseSkillIds(rawPayload.skills),
        skill_details: this.normaliseSkillDetails(rawPayload.skills),
        item_size: this.normaliseRelatedField(rawPayload.item_size),
        container_size: this.normaliseRelatedField(rawPayload.container_size),
        artefact_type: this.normaliseRelatedField(rawPayload.artefact_type),
        arena_id:
          rawPayload.arena_id ??
          rawPayload.arena ??
          this.arena?.id ??
          this.getCampaign()?.id ??
          null,
      }

      if (rawPayload.race) {
        payload.race = this.normaliseRelatedField(rawPayload.race)
      }

      const normalisedBaseArtefact = this.normaliseRelatedField(
        rawPayload.artefact_base_item,
      )
      const normalisedSourceItem = this.normaliseRelatedField(rawPayload.item)

      if (normalisedBaseArtefact) {
        payload.artefact_base_item = normalisedBaseArtefact
      } else if (
        sourceType &&
        sourceType !== 'Artefact' &&
        normalisedSourceItem
      ) {
        payload.artefact_base_item = normalisedSourceItem
      }

      const cleanedPayload = this.stripSubtypeSpecificArtefactFields(
        payload,
        rawPayload,
      )

      Object.keys(cleanedPayload).forEach((key) => {
        if (cleanedPayload[key] === null || cleanedPayload[key] === undefined) {
          delete cleanedPayload[key]
        }
      })

      return cleanedPayload
    },
    buildCreateRequestPayload(rawPayload = {}) {
      const payload = this.buildCampaignArtefactCreatePayload(rawPayload)
      const imageFilePayload = rawPayload?.image_file
      const hasImageUpload =
        imageFilePayload instanceof FormData &&
        Array.from(imageFilePayload.keys()).length > 0

      if (!hasImageUpload) {
        return payload
      }

      const multipartPayload = new FormData()

      Object.entries(payload).forEach(([key, value]) => {
        if (value === null || value === undefined) {
          return
        }

        if (key === 'skill_details') {
          multipartPayload.append(key, JSON.stringify(value))
          return
        }

        if (Array.isArray(value)) {
          value.forEach((entry) => {
            multipartPayload.append(key, entry)
          })
          return
        }

        multipartPayload.append(key, value)
      })

      const imageFile = imageFilePayload.get('image')
      if (imageFile) {
        multipartPayload.append('image', imageFile)
      }

      return multipartPayload
    },
    buildCampaignArtefactSavePayload(rawPayload = {}) {
      const sourceType = rawPayload.type || rawPayload.actual_type || 'Artefact'
      const payload = {
        name: rawPayload.name ?? '',
        type: 'Artefact',
        dm_notes: rawPayload.dm_notes ?? rawPayload.notes ?? '',
        short_description: rawPayload.short_description ?? '',
        description: rawPayload.description ?? '',
        active: rawPayload.active ?? true,
        status:
          rawPayload.status ?? ((rawPayload.active ?? true) ? 'Active' : null),
        campaign: true,
        starter: rawPayload.starter ?? false,
        tallyable: rawPayload.tallyable ?? false,
        tally_qty: rawPayload.tally_qty ?? 0,
        wearable: rawPayload.wearable ?? false,
        storeable: rawPayload.storeable ?? true,
        storage: rawPayload.storage ?? false,
        attachable: rawPayload.attachable ?? false,
        attach_point: rawPayload.attach_point ?? false,
        countable: rawPayload.countable ?? false,
        unit_name: rawPayload.unit_name ?? '',
        max_unit_count: rawPayload.max_unit_count ?? 0,
        estimated_value: rawPayload.estimated_value ?? false,
        enc: rawPayload.enc ?? 0.01,
        ap: rawPayload.ap ?? 0,
        hp: rawPayload.hp ?? 0,
        range: rawPayload.range ?? '',
        load: rawPayload.load ?? 0,
        damage_dice: rawPayload.damage_dice ?? '',
        impale: this.normaliseBooleanField(rawPayload.impale, false),
        set_charge: this.normaliseBooleanField(rawPayload.set_charge, false),
        poison_type: rawPayload.poison_type ?? null,
        pot: rawPayload.pot ?? 0,
        effect: rawPayload.effect ?? '',
        delay: rawPayload.delay ?? '',
        duration: rawPayload.duration ?? '',
        resist: rawPayload.resist ?? '',
        healing_type: rawPayload.healing_type ?? null,
        recovers: rawPayload.recovers ?? '',
        value_lower: rawPayload.value_lower ?? 0,
        value_upper: rawPayload.value_upper ?? 0,
        currency: this.normaliseRelatedField(rawPayload.currency),
        pwr_storage: rawPayload.pwr_storage ?? 0,
        material: rawPayload.material ?? 'Unknown',
        age: rawPayload.age ?? 'Unknown',
        quality: rawPayload.quality ?? 'Unknown',
        trait: this.normaliseRelatedField(rawPayload.trait),
        skill_ids: this.normaliseSkillIds(rawPayload.skills),
        skill_details: this.normaliseSkillDetails(rawPayload.skills),
        item_size: this.normaliseRelatedField(rawPayload.item_size),
        container_size: this.normaliseRelatedField(rawPayload.container_size),
        artefact_type: this.normaliseRelatedField(rawPayload.artefact_type),
        image: rawPayload.image ?? null,
        arena_id:
          rawPayload.arena_id ??
          rawPayload.arena ??
          this.arena?.id ??
          this.getCampaign()?.id ??
          null,
      }

      if (rawPayload.race !== undefined) {
        payload.race = this.normaliseRelatedField(rawPayload.race)
      }

      if (rawPayload.artefact_base_item !== undefined) {
        payload.artefact_base_item = this.normaliseRelatedField(
          rawPayload.artefact_base_item,
        )
      } else if (sourceType && sourceType !== 'Artefact' && rawPayload.item) {
        payload.artefact_base_item = this.normaliseRelatedField(rawPayload.item)
      }

      const cleanedPayload = this.stripSubtypeSpecificArtefactFields(
        payload,
        rawPayload,
      )

      Object.keys(cleanedPayload).forEach((key) => {
        if (cleanedPayload[key] === undefined) {
          delete cleanedPayload[key]
        }
      })

      return cleanedPayload
    },
    buildCampaignArtefactPatchPayload(change) {
      if (change?.key === 'image_file') {
        return change?.value instanceof FormData ? change.value : null
      }

      const keyMap = {
        active: 'active',
        ap: 'ap',
        age: 'age',
        attach_point: 'attach_point',
        attachable: 'attachable',
        artefact_base_item: 'artefact_base_item',
        artefact_type: 'artefact_type',
        container_size: 'container_size',
        countable: 'countable',
        currency: 'currency',
        damage_dice: 'damage_dice',
        delay: 'delay',
        dm_notes: 'dm_notes',
        description: 'description',
        duration: 'duration',
        enc: 'enc',
        estimated_value: 'estimated_value',
        effect: 'effect',
        healing_type: 'healing_type',
        hp: 'hp',
        image: 'image',
        status: 'status',
        impale: 'impale',
        item_size: 'item_size',
        load: 'load',
        material: 'material',
        max_unit_count: 'max_unit_count',
        name: 'name',
        poison_type: 'poison_type',
        pot: 'pot',
        pwr_storage: 'pwr_storage',
        quality: 'quality',
        race: 'race',
        range: 'range',
        recovers: 'recovers',
        resist: 'resist',
        set_charge: 'set_charge',
        short_description: 'short_description',
        skills: 'skill_details',
        starter: 'starter',
        storage: 'storage',
        storeable: 'storeable',
        tally_qty: 'tally_qty',
        tallyable: 'tallyable',
        trait: 'trait',
        unit_name: 'unit_name',
        value_lower: 'value_lower',
        value_upper: 'value_upper',
        wearable: 'wearable',
      }

      const mappedKey = keyMap[change?.key]
      if (!mappedKey) {
        return null
      }

      let value = change?.value
      if (mappedKey === 'skill_details') {
        value = this.normaliseSkillDetails(value)
      } else if (
        [
          'active',
          'starter',
          'tallyable',
          'wearable',
          'storeable',
          'countable',
          'estimated_value',
          'storage',
          'attachable',
          'attach_point',
          'impale',
          'set_charge',
        ].includes(mappedKey)
      ) {
        value = this.normaliseBooleanField(value, false)
      } else if (
        [
          'artefact_type',
          'currency',
          'trait',
          'race',
          'item_size',
          'container_size',
          'artefact_base_item',
        ].includes(mappedKey)
      ) {
        value = this.normaliseRelatedField(value)
      } else if (mappedKey === 'unit_name') {
        value = value ?? ''
      } else if (['value_upper', 'value_lower'].includes(mappedKey)) {
        value =
          value === null || value === '' || value === undefined
            ? 0
            : Number(value)
      }

      return {
        [mappedKey]: value,
      }
    },
    buildCampaignArtefactPatchPayloadFromChanges(changes = []) {
      const sourceChanges = Array.isArray(changes) ? changes : []
      const payload = {}
      let formDataPayload = null

      sourceChanges.forEach((change) => {
        const mappedPayload = this.buildCampaignArtefactPatchPayload(change)

        if (!mappedPayload) {
          return
        }

        if (mappedPayload instanceof FormData) {
          formDataPayload = mappedPayload
          return
        }

        Object.assign(payload, mappedPayload)
      })

      if (formDataPayload) {
        Object.entries(payload).forEach(([key, value]) => {
          if (value === undefined || value === null) {
            return
          }

          if (key === 'skill_details') {
            formDataPayload.append(key, JSON.stringify(value))
            return
          }

          if (Array.isArray(value)) {
            value.forEach((entry) => {
              formDataPayload.append(key, entry)
            })
            return
          }

          formDataPayload.append(key, value)
        })

        return formDataPayload
      }

      return Object.keys(payload).length > 0 ? payload : null
    },
    enqueueCampaignArtefactUpdate(id, callback) {
      const queueKey = String(id)
      const previousChain =
        this.updateChainsByArtefactId[queueKey] || Promise.resolve()

      const nextChain = previousChain.catch(() => {}).then(() => callback())

      this.updateChainsByArtefactId[queueKey] = nextChain

      return nextChain.finally(() => {
        if (this.updateChainsByArtefactId[queueKey] === nextChain) {
          delete this.updateChainsByArtefactId[queueKey]
        }
      })
    },
    applyLocalPatchToCampaignArtefactRow(id, payload) {
      if (!id || !payload || payload instanceof FormData) {
        return
      }

      const index = this.artefacts.findIndex((row) => row.id === id)
      if (index === -1) {
        return
      }

      const nextArtefacts = [...this.artefacts]
      const existingRow = nextArtefacts[index] || {}
      const mergedRow = {
        ...existingRow,
        ...payload,
      }

      const recalculateTotEnc =
        Object.prototype.hasOwnProperty.call(payload, 'enc') ||
        Object.prototype.hasOwnProperty.call(payload, 'tally_qty') ||
        Object.prototype.hasOwnProperty.call(payload, 'tot_enc')

      const patchedRow = this.normaliseArtefactRow(mergedRow, {
        recalculateTotEnc,
      })

      nextArtefacts.splice(index, 1, patchedRow)
      this.artefacts = nextArtefacts

      if (this.artefact?.id === id) {
        this.artefact = this.normaliseArtefactRow(
          {
            ...this.artefact,
            ...payload,
          },
          {
            recalculateTotEnc,
          },
        )
      }
    },
    upsertCampaignArtefactRow(artefact) {
      if (!artefact?.id) return

      const normalisedArtefact = this.normaliseArtefactRow(artefact)

      const index = this.artefacts.findIndex((row) => row.id === artefact.id)
      if (index === -1) {
        this.artefacts = [...this.artefacts, normalisedArtefact]
      } else {
        const nextArtefacts = [...this.artefacts]
        const mergedArtefact = this.normaliseArtefactRow({
          ...nextArtefacts[index],
          ...normalisedArtefact,
        })
        nextArtefacts.splice(index, 1, mergedArtefact)
        this.artefacts = nextArtefacts
      }

      if (this.artefact?.id === artefact.id) {
        this.artefact = this.normaliseArtefactRow({
          ...this.artefact,
          ...normalisedArtefact,
        })
      }
    },
    //========================================================
    // add campaign artefact harness
    //-------------------------------
    async addCampaignArtefactHarness(new_artefact) {
      console.log('-------------------------------')
      console.log('IN addCampaignArtefactHarness = ' + new_artefact?.name)

      const request = {
        action: 'ADD',
        payload: this.buildCreateRequestPayload(new_artefact?.payload),
      }

      await useEditCampaignArtefact(request)
        .then(async (response) => {
          const savedArtefact = response?.data ?? {}

          // Optimistic insert so the new row appears immediately.
          this.upsertCampaignArtefactRow(savedArtefact)

          // Then re-read from backend using the same path as UPDATE
          // to keep table state aligned with persisted values.
          const arenaId = this.arena?.id ?? null
          await this.loadStaticCampaignArtefacts(arenaId)
          this.artefacts = this.normaliseArtefactRows(
            this.getCampaignArtefacts(),
          )

          // If the refreshed list still does not contain the new row
          // (e.g. eventual consistency/caching), keep the optimistic row.
          if (
            savedArtefact?.id &&
            !this.artefacts.some((row) => row.id === savedArtefact.id)
          ) {
            this.upsertCampaignArtefactRow(savedArtefact)
          }

          this.loadReady = true
          this.$nextTick(() => {
            this.refreshArtefactsScrollState()
          })
          this.emitter.emit('campaign-artefacts-updated')

          showNotification(
            this.formatArtefactActionMessage(savedArtefact, 'added'),
          )
        })
        .catch((err) => {
          console.error(
            'addCampaignArtefactHarness validation error payload:',
            err?.response?.data,
          )
          handleError(err, 38)
        })
    },
    //========================================================
    // update campaign artefact harness
    //----------------------------------
    async updateCampaignArtefactHarness(updated_artefact) {
      console.log('-------------------------------')
      console.log('IN updateCampaignArtefactHarness')

      const hasBatchedChanges = Array.isArray(updated_artefact?.changes)
      const payload = updated_artefact?.payload
        ? this.buildCampaignArtefactSavePayload(updated_artefact.payload)
        : hasBatchedChanges
          ? this.buildCampaignArtefactPatchPayloadFromChanges(
              updated_artefact.changes,
            )
          : this.buildCampaignArtefactPatchPayload(updated_artefact)
      if (!updated_artefact?.id || !payload) {
        return
      }

      const isPartialUpdate =
        updated_artefact?.partial !== undefined
          ? !!updated_artefact.partial
          : !updated_artefact?.payload

      if (isPartialUpdate) {
        this.applyLocalPatchToCampaignArtefactRow(updated_artefact.id, payload)
      }

      await this.enqueueCampaignArtefactUpdate(
        updated_artefact.id,
        async () => {
          await useEditCampaignArtefact({
            action: 'UPDATE',
            id: updated_artefact.id,
            payload,
            partial: isPartialUpdate,
          })
            .then(async (response) => {
              const savedArtefact = response?.data ?? {}

              this.upsertCampaignArtefactRow(savedArtefact)

              // Always re-read campaign artefacts after save so UI state
              // cannot drift from persisted backend values.
              const arenaId = this.arena?.id ?? null
              await this.loadStaticCampaignArtefacts(arenaId)
              this.artefacts = this.normaliseArtefactRows(
                this.getCampaignArtefacts(),
              )

              if (this.artefact?.id === updated_artefact.id) {
                const refreshedArtefact = this.artefacts.find(
                  (row) => row.id === updated_artefact.id,
                )
                if (refreshedArtefact) {
                  this.artefact = refreshedArtefact
                }
              }

              const messageArtefact =
                this.artefacts.find((row) => row.id === updated_artefact.id) ??
                savedArtefact
              this.emitter.emit('campaign-artefacts-updated')
              showNotification(
                this.formatArtefactActionMessage(messageArtefact, 'updated'),
              )
            })
            .catch((err) => {
              handleError(err, 47)
            })
        },
      )
    },
    //========================================================
    // delete campaign artefact harness
    //---------------------
    async deleteCampaignArtefactHarness(artefact) {
      console.log('----------------')
      console.log('IN deleteCampaignArtefactHarness artefact = ', artefact)

      if (!artefact?.id) {
        return
      }

      await useEditCampaignArtefact({
        action: 'DELETE',
        id: artefact.id,
      })
        .then(() => {
          this.artefacts = this.artefacts.filter(
            (row) => row.id !== artefact.id,
          )
          if (this.artefact?.id === artefact.id) {
            this.artefact = null
            this.isSidePanelOpen = false
          }
          this.emitter.emit('campaign-artefacts-updated')
          showNotification(
            this.formatArtefactActionMessage(artefact, 'deleted'),
          )
        })
        .catch((err) => {
          handleError(err, 40)
        })
    },
    //========================================================
    // load config handler
    //---------------------
    async loadConfigHandler() {
      //----------
      // api call
      //----------
      await this.loadConfig()
        //------------
        // successful
        //------------
        .then(() => {
          this.config = this.getConfig()
        })
        //-------
        // error
        //-------
        .catch((err) => {
          handleError(err, 41)
        })
    },
    //========================================================
    // load static campaign artefacts handler
    //----------------------------------------
    async loadStaticCampaignArtefactsHandler() {
      this.loadReady = false
      const arenaId = this.arena?.id ?? null
      //---------------
      // make API call
      //---------------
      let api_call = this.loadStaticCampaignArtefacts(arenaId)
      //---------------
      // await results
      //---------------
      await api_call
        .then(() => {
          //-------------------------------
          // get stored campaign artefacts
          //-------------------------------
          this.artefacts = this.normaliseArtefactRows(
            this.getCampaignArtefacts(),
          )
          console.log('xxxx this.artefacts = ', this.artefacts)
          this.loadReady = true

          this.$nextTick(() => {
            setTimeout(() => {
              this.refreshArtefactsScrollState()
            }, 300)
          })
        })
        .catch((err) => {
          this.loadReady = true
          handleError(err, 42)
        })
    },
  },
  //========================================================
  // mounted
  //---------
  mounted() {
    //----------------------
    // get campaign handler
    //---------------------
    this.getCampaignHandler()
    //----------------------------------------
    // load static campaign artefacts handler
    //----------------------------------------
    this.loadStaticCampaignArtefactsHandler()
    //----------------------------
    // set campaign artefact view
    //----------------------------
    this.setView('campaign_artefacts')
    //========================================================
    // show campaign artefact panel handler
    //--------------------------------------
    this.emitter.on('show-artefact-panel', () => {
      console.log('-----------------------------')
      console.log('IN show campaign artefact panel handler')
      this.showArtefactPanel()
    })
    //========================================================
    // sidepanel detect emit
    //-----------------------
    this.emitter.on('toggle-sidepanel-artefact', (artefact) => {
      this.artefact = artefact
      this.isSidePanelOpen = true
    })
    //========================================================
    // exit view handler
    //-------------------
    this.emitter.on('exit-artefact-view', () => {
      this.loadStaticCampaignArtefactsHandler()
      this.exitView()
    })
    //========================================================
    // next campaign artefact emit
    //-----------------------------
    this.emitter.on('next-artefact', (artefact) => {
      this.artefact = getNext(artefact.id, this.artefacts)
      this.emitter.emit('load-artefact', this.artefact)
    })
    //========================================================
    // previous campaign artefact emit
    //---------------------------------
    this.emitter.on('previous-artefact', (artefact) => {
      this.artefact = getPrevious(artefact.id, this.artefacts)
      this.emitter.emit('load-artefact', this.artefact)
    })
    //========================================================
    // close side panel
    //------------------
    this.emitter.on('close-artefact-side-panel', (ind) => {
      this.isSidePanelOpen = ind
    })
    //========================================================
    // add campaign artefact
    //-----------------------
    this.emitter.on('add-artefact', (parms) => {
      //--------------------------------------
      // perform campaign artefact call (add)
      //--------------------------------------
      this.addCampaignArtefactHarness(parms.artefact)
    })
    //========================================================
    // update campaign artefact
    //--------------------------
    this.emitter.on('update-artefact', (parms) => {
      //-----------------------------------------
      // perform campaign artefact call (update)
      //-----------------------------------------
      this.updateCampaignArtefactHarness(parms.artefact, parms.chars_changed)
    })
    //========================================================
    // delete campaign artefact
    //--------------------------
    this.emitter.on('delete-artefact', (delete_index) => {
      //-----------------------------------------
      // perform campaign artefact call (delete)
      //-----------------------------------------
      this.deleteCampaignArtefactHarness(delete_index)
    })
  },
}
</script>

<style scoped>
.bb-sheet-header-row {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 2.75rem;
  margin-bottom: 0.35rem;
}

.bb-sheet-search-slot {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
}

.bb-sheet-search-wrap {
  min-width: 18rem;
}

.bb-sheet-search-shell {
  margin-bottom: 0;
}

.bb-sheet-search-input {
  min-width: 14rem;
  width: 100%;
}

.bb-sheet-header-row .bb-sheet-title {
  width: 100%;
  text-align: center;
}

@media (max-width: 768px) {
  .bb-sheet-header-row {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
  }

  .bb-sheet-search-slot {
    position: static;
    transform: none;
  }
}
</style>
